<div class="mx-auto flex flex-col gap-6">
  <p-card styleClass="rounded-3xl shadow-xl border border-white bg-white/90 backdrop-blur-sm overflow-hidden">
    <ng-template pTemplate="header">
      <div class="px-6 pt-6 pb-2 flex flex-col gap-2">
        <div class="flex items-center gap-3">
          <div
            class="h-12 w-12 rounded-2xl bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white flex items-center justify-center shadow-md">
            <i class="pi pi-file-excel text-xl"></i>
          </div>

          <div>
            <h2 class="text-xl md:text-2xl font-bold text-slate-900 m-0">T3 Model File Upload</h2>
            <p class="text-sm text-slate-500 m-0">
              Upload <span class="font-semibold">city_level_historical_data</span> file, validate, preview, and submit.
            </p>
          </div>
        </div>
      </div>
    </ng-template>

    <div class="px-6 pb-6 flex flex-col gap-5">
      @if (errorMsg) {
      <p-message severity="error" [text]="errorMsg"></p-message>
      }

      @if (successMsg) {
      <p-message severity="success" [text]="successMsg"></p-message>
      }

      <div class="rounded-3xl bg-white">
  <div class="grid grid-cols-1 xl:grid-cols-[1fr_auto] gap-4 items-stretch">
    <div
      class="rounded-2xl border p-4 transition-all duration-200"
      [class.border-rose-200]="cityLevelFileName && cityLevelHasErrors"
      [class.bg-rose-50]="cityLevelFileName && cityLevelHasErrors"
      [class.border-emerald-200]="cityLevelFileName && !cityLevelHasErrors"
      [class.bg-emerald-50]="cityLevelFileName && !cityLevelHasErrors"
      [class.border-slate-200]="!cityLevelFileName"
      [class.bg-white]="!cityLevelFileName"
    >
      <div class="flex items-start justify-between gap-4">
        <div class="min-w-0">
          <div class="flex items-center gap-3">
            <span
              class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl"
              [class.bg-rose-100]="cityLevelFileName && cityLevelHasErrors"
              [class.text-rose-700]="cityLevelFileName && cityLevelHasErrors"
              [class.bg-emerald-100]="cityLevelFileName && !cityLevelHasErrors"
              [class.text-emerald-700]="cityLevelFileName && !cityLevelHasErrors"
              [class.bg-slate-100]="!cityLevelFileName"
              [class.text-slate-500]="!cityLevelFileName"
            >
              <i class="pi" [ngClass]="cityLevelStatusIcon"></i>
            </span>

            <div class="min-w-0">
              <div class="text-sm font-semibold text-slate-900">City Level Historical Data</div>
              <div
                class="text-xs truncate"
                [class.text-rose-700]="cityLevelFileName && cityLevelHasErrors"
                [class.text-emerald-700]="cityLevelFileName && !cityLevelHasErrors"
                [class.text-slate-500]="!cityLevelFileName"
              >
                {{ cityLevelFileName || 'Upload city_level_historical_data.csv, .xlsx or .xls' }}
              </div>
              <div class="mt-1 text-[11px] text-slate-500">
                Single sheet containing historical data rows and input time series rows.
              </div>
            </div>
          </div>
        </div>

        @if (cityLevelFileName) {
        <span
          class="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold"
          [class.bg-rose-100]="cityLevelHasErrors"
          [class.text-rose-700]="cityLevelHasErrors"
          [class.bg-amber-100]="cityLevelHasWarnings && !cityLevelHasErrors"
          [class.text-amber-700]="cityLevelHasWarnings && !cityLevelHasErrors"
          [class.bg-emerald-100]="!cityLevelHasErrors && !cityLevelHasWarnings"
          [class.text-emerald-700]="!cityLevelHasErrors && !cityLevelHasWarnings"
        >
          {{ cityLevelStatusLabel }}
        </span>
        }
      </div>

      <div class="mt-4 flex flex-wrap items-center gap-3">
        <input
          #cityLevelFileInput
          type="file"
          accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          class="hidden"
          (change)="onCityLevelFileSelected($event)"
        />

        @if (!cityLevelFileName) {
        <button
          pButton
          type="button"
          label="Upload File"
          icon="pi pi-upload"
          class="p-button-rounded !border-0 !px-4 !py-2 !text-sm !font-semibold !text-white"
          style="background: linear-gradient(to right, rgb(244 63 94), rgb(217 70 239)); border: 0; color: white;"
          [disabled]="loading"
          (click)="triggerCityLevelFilePicker()"
        ></button>
        } @else {
        <button
          pButton
          type="button"
          label="Clear"
          icon="pi pi-times"
          class="p-button-outlined p-button-danger p-button-sm"
          [disabled]="loading"
          (click)="clearCityLevelFile()"
        ></button>
        }

        @if (cityLevelFileName && cityLevelHasErrors) {
        <button
          type="button"
          class="text-xs font-semibold text-rose-600 underline underline-offset-4 hover:text-fuchsia-600"
          (click)="openIssuesDialog()"
        >
          View errors
        </button>
        }

        <button
          type="button"
          class="text-xs font-medium text-slate-600 underline underline-offset-4 decoration-slate-300 hover:text-rose-600"
          (click)="downloadCityLevelTemplate()"
        >
          Download template
        </button>
      </div>
    </div>

    <div class="rounded-2xl border border-slate-100 bg-slate-50 p-4 flex flex-col justify-between gap-4">
      <div>
        <div class="text-xs font-semibold uppercase tracking-wide text-slate-500">Submit</div>
        <div class="mt-1 text-xs text-slate-500">
          The merged city level file is submitted as a single upload.
        </div>
      </div>

      <button
        pButton
        type="button"
        label="Submit uploaded file"
        icon="pi pi-send"
        class="p-button-rounded !border-0 !px-4 !py-2.5 !text-sm !font-semibold !text-white !shadow-sm transition-all duration-200 hover:!shadow-md disabled:!opacity-60"
        style="background: linear-gradient(to right, rgb(79 70 229), rgb(37 99 235)); border: 0; color: white;"
        [disabled]="!canSubmit || loading"
        (click)="submitFiles()"
      ></button>

      @if (blockingIssues.length || warningIssues.length) {
      <button
        type="button"
        class="self-start text-xs font-semibold text-rose-600 underline underline-offset-4 hover:text-fuchsia-600"
        (click)="openIssuesDialog()"
      >
        View validation issues
      </button>
      }
    </div>
  </div>
</div>
    </div>
  </p-card>

  @if (uploadStatusCount > 0) {
  @if (inputsFile.parsed) {

  <p-card styleClass="rounded-3xl shadow-xl border border-white bg-white/95 backdrop-blur-sm overflow-hidden">
    <ng-template pTemplate="header">
      <div class="px-6 pt-6 pb-2">
        <h3 class="text-lg font-bold text-slate-900 m-0">Inputs Time Series Visualizations</h3>
        <p class="text-xs text-slate-500 m-0 mt-1">
          Interactive view of total and gender-specific population distributions by year.
        </p>
      </div>
    </ng-template>

    <div class="px-4 md:px-6 pb-6">
      @if (graph1Data && graph2Data) {
      <div class="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <div class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div class="flex items-center justify-between mb-4 gap-3">
            <div>
              <div class="text-sm font-semibold text-slate-900">Graph 1</div>
              <div class="text-xs text-slate-500">Total population by year</div>
            </div>

            <div class="inline-flex items-center rounded-full border border-rose-100 bg-rose-50 p-1 shadow-sm">
              <button type="button" class="rounded-full px-3 py-1 text-xs font-semibold transition-all duration-200"
                [class.bg-gradient-to-r]="graph1Type === 'line'" [class.from-rose-500]="graph1Type === 'line'"
                [class.to-fuchsia-500]="graph1Type === 'line'" [class.text-white]="graph1Type === 'line'"
                [class.shadow-sm]="graph1Type === 'line'" [class.text-slate-600]="graph1Type !== 'line'"
                (click)="setGraph1Type('line')">
                Line
              </button>

              <button type="button" class="rounded-full px-3 py-1 text-xs font-semibold transition-all duration-200"
                [class.bg-gradient-to-r]="graph1Type === 'bar'" [class.from-rose-500]="graph1Type === 'bar'"
                [class.to-fuchsia-500]="graph1Type === 'bar'" [class.text-white]="graph1Type === 'bar'"
                [class.shadow-sm]="graph1Type === 'bar'" [class.text-slate-600]="graph1Type !== 'bar'"
                (click)="setGraph1Type('bar')">
                Bar
              </button>
            </div>
          </div>

          <div class="h-[320px]">
            <p-chart [type]="graph1Type" [data]="graph1Data" [options]="graph1Options"></p-chart>
          </div>
        </div>

        <div class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div class="flex items-center justify-between mb-4 gap-3">
            <div>
              <div class="text-sm font-semibold text-slate-900">Graph 2</div>
              <div class="text-xs text-slate-500">Male vs Female population by year</div>
            </div>

            <div class="inline-flex items-center rounded-full border border-rose-100 bg-rose-50 p-1 shadow-sm">
              <button type="button" class="rounded-full px-3 py-1 text-xs font-semibold transition-all duration-200"
                [class.bg-gradient-to-r]="graph2Type === 'line'" [class.from-rose-500]="graph2Type === 'line'"
                [class.to-fuchsia-500]="graph2Type === 'line'" [class.text-white]="graph2Type === 'line'"
                [class.shadow-sm]="graph2Type === 'line'" [class.text-slate-600]="graph2Type !== 'line'"
                (click)="setGraph2Type('line')">
                Line
              </button>

              <button type="button" class="rounded-full px-3 py-1 text-xs font-semibold transition-all duration-200"
                [class.bg-gradient-to-r]="graph2Type === 'bar'" [class.from-rose-500]="graph2Type === 'bar'"
                [class.to-fuchsia-500]="graph2Type === 'bar'" [class.text-white]="graph2Type === 'bar'"
                [class.shadow-sm]="graph2Type === 'bar'" [class.text-slate-600]="graph2Type !== 'bar'"
                (click)="setGraph2Type('bar')">
                Bar
              </button>
            </div>
          </div>

          <div class="h-[320px]">
            <p-chart [type]="graph2Type" [data]="graph2Data" [options]="graph2Options"></p-chart>
          </div>
        </div>
      </div>
      } @else {
      <div class="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-slate-500">
        <i class="pi pi-chart-line text-3xl mb-3 text-slate-400"></i>
        <div class="text-sm font-medium">No graph data available yet</div>
        <div class="text-xs mt-1">
          Upload a valid Inputs Time Series file containing required population variables to visualize charts.
        </div>
      </div>
      }
    </div>
  </p-card>
  }

  @if (hasHistoricalGraph) {
  <div class="grid grid-cols-1 xl:grid-cols-2 gap-6">
    <p-card styleClass="rounded-3xl shadow-xl border border-white bg-white/95 backdrop-blur-sm overflow-hidden h-full">
      <ng-template pTemplate="header">
        <div class="px-6 pt-6 pb-2">
          <h3 class="text-lg font-bold text-slate-900 m-0">Historical Aggregates Summary</h3>
          <p class="text-xs text-slate-500 m-0 mt-1">
            Historical Data and Uploaded Data totals across all available years.
          </p>
        </div>
      </ng-template>

      <div class="px-4 md:px-6 pb-6">
  <div class="mb-4 flex flex-wrap items-center gap-2">
    @for (year of historicalAggregateYears; track year) {
    <button
      type="button"
      class="rounded-full px-3 py-1.5 text-xs font-semibold transition-all duration-200"
      [class.bg-gradient-to-r]="activeHistoricalAggregateYear === year"
      [class.from-rose-500]="activeHistoricalAggregateYear === year"
      [class.to-fuchsia-500]="activeHistoricalAggregateYear === year"
      [class.text-white]="activeHistoricalAggregateYear === year"
      [class.shadow-sm]="activeHistoricalAggregateYear === year"
      [class.bg-slate-100]="activeHistoricalAggregateYear !== year"
      [class.text-slate-600]="activeHistoricalAggregateYear !== year"
      (click)="setHistoricalAggregateYear(year)"
    >
      {{ year }}
    </button>
    }

    <button
      type="button"
      class="rounded-full px-3 py-1.5 text-xs font-semibold transition-all duration-200"
      [class.bg-gradient-to-r]="activeHistoricalAggregateYear === 'all'"
      [class.from-rose-500]="activeHistoricalAggregateYear === 'all'"
      [class.to-fuchsia-500]="activeHistoricalAggregateYear === 'all'"
      [class.text-white]="activeHistoricalAggregateYear === 'all'"
      [class.shadow-sm]="activeHistoricalAggregateYear === 'all'"
      [class.bg-rose-50]="activeHistoricalAggregateYear !== 'all'"
      [class.text-slate-600]="activeHistoricalAggregateYear !== 'all'"
      (click)="setHistoricalAggregateYear('all')"
    >
      Show all years
    </button>
  </div>

  @if (activeHistoricalAggregateYear !== 'all') {
  <div class="overflow-hidden rounded-2xl border border-rose-100 bg-white shadow-sm">
    <div class="grid grid-cols-5 bg-rose-50 px-3 py-2 text-xs font-semibold text-slate-700">
      <div>Year</div>
      <div>Label</div>
      <div class="text-right">Historical Data</div>
      <div class="text-right">Uploaded Data</div>
      <div class="text-right">Difference</div>
    </div>

    @if (hasHistoricalAggregateRows) {
    @for (item of historicalAggregates; track item.year + item.label) {
    <div class="grid grid-cols-5 items-center border-t border-rose-50 px-3 py-2 text-xs">
      <div class="font-semibold text-slate-500">{{ item.year }}</div>
      <div class="font-medium text-slate-700">{{ item.label }}</div>
      <div class="text-right font-semibold text-rose-700">{{ item.historicalData }}</div>
      <div class="text-right font-semibold text-blue-700">{{ item.uploadedData }}</div>
      <div class="text-right">
        <span
          class="rounded-full px-2 py-1 text-[11px] font-semibold"
          [ngClass]="getDifferenceClass(item.differenceValue)"
        >
          {{ item.difference }}
        </span>
      </div>
    </div>
    }
    } @else {
    <div class="px-4 py-6 text-center text-xs text-slate-500">
      No historical aggregate data available.
    </div>
    }
  </div>
  }

  @if (activeHistoricalAggregateYear === 'all') {
  <div class="rounded-2xl border border-rose-100 bg-white shadow-sm overflow-hidden">
    <div class="bg-rose-50 px-4 py-3">
      <div class="text-xs font-semibold text-slate-700">All years compact comparison</div>
      <div class="text-[11px] text-slate-500 mt-1">
        Each metric shows yearly Historical Data, Uploaded Data, and Difference in compact chips.
      </div>
    </div>

    <div class="max-h-[28rem] overflow-auto divide-y divide-slate-100">
      @if (hasHistoricalAggregateRows) {
      @for (row of historicalAggregateAllYearRows; track row.label) {
      <div class="p-4">
        <div class="mb-3 text-sm font-semibold text-slate-800">
          {{ row.label }}
        </div>

        <div class="flex gap-2 overflow-x-auto pb-1">
          @for (item of row.comparisons; track item.year) {
          <div class="min-w-[9.5rem] rounded-2xl border border-slate-100 bg-slate-50 p-3">
            <div class="mb-2 flex items-center justify-between">
              <span class="text-xs font-bold text-slate-700">{{ item.year }}</span>
              <span
                class="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                [ngClass]="getDifferenceClass(item.differenceValue)"
              >
                Δ {{ item.difference }}
              </span>
            </div>

            <div class="space-y-1 text-[11px]">
              <div class="flex items-center justify-between gap-2">
                <span class="text-slate-500">Historical</span>
                <span class="font-semibold text-rose-700">{{ item.historicalData }}</span>
              </div>

              <div class="flex items-center justify-between gap-2">
                <span class="text-slate-500">Uploaded</span>
                <span class="font-semibold text-blue-700">{{ item.uploadedData }}</span>
              </div>
            </div>
          </div>
          }
        </div>
      </div>
      }
      } @else {
      <div class="px-4 py-6 text-center text-xs text-slate-500">
        No historical aggregate data available.
      </div>
      }
    </div>
  </div>
  }
</div>
    </p-card>

    <p-card styleClass="rounded-3xl shadow-xl border border-white bg-white/95 backdrop-blur-sm overflow-hidden h-full">
      <ng-template pTemplate="header">
        <div class="px-6 pt-6 pb-2">
          <h3 class="text-lg font-bold text-slate-900 m-0">Historical Data Visualization</h3>
          <p class="text-xs text-slate-500 m-0 mt-1">
            Compare city historical data with uploaded data by year.
          </p>
        </div>
      </ng-template>

      <div class="px-4 md:px-6 pb-6">
        <div class="flex flex-col gap-4">
          <div class="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1">
              <p-select [options]="historicalMetricOptions" [(ngModel)]="selectedHistoricalMetric" optionLabel="label"
                optionValue="value" appendTo="body" placeholder="Select metric" class="w-full"
                (onChange)="onHistoricalMetricChange()"></p-select>

              <p-select [options]="historicalSeriesOptions" [(ngModel)]="selectedHistoricalSeries" optionLabel="label"
                optionValue="value" appendTo="body" placeholder="Select series" class="w-full"
                (onChange)="onHistoricalSeriesChange()"></p-select>
            </div>

            <div
              class="inline-flex items-center rounded-full border border-rose-100 bg-rose-50 p-1 shadow-sm self-start xl:self-auto">
              <button type="button" class="rounded-full px-3 py-1 text-xs font-semibold transition-all duration-200"
                [class.bg-gradient-to-r]="historicalGraphType === 'line'"
                [class.from-rose-500]="historicalGraphType === 'line'"
                [class.to-fuchsia-500]="historicalGraphType === 'line'"
                [class.text-white]="historicalGraphType === 'line'" [class.shadow-sm]="historicalGraphType === 'line'"
                [class.text-slate-600]="historicalGraphType !== 'line'" (click)="setHistoricalGraphType('line')">
                Line
              </button>

              <button type="button" class="rounded-full px-3 py-1 text-xs font-semibold transition-all duration-200"
                [class.bg-gradient-to-r]="historicalGraphType === 'bar'"
                [class.from-rose-500]="historicalGraphType === 'bar'"
                [class.to-fuchsia-500]="historicalGraphType === 'bar'"
                [class.text-white]="historicalGraphType === 'bar'" [class.shadow-sm]="historicalGraphType === 'bar'"
                [class.text-slate-600]="historicalGraphType !== 'bar'" (click)="setHistoricalGraphType('bar')">
                Bar
              </button>
            </div>
          </div>

          <div class="h-[320px]">
            <p-chart [type]="historicalGraphType" [data]="historicalGraphData"
              [options]="historicalGraphOptions"></p-chart>
          </div>
        </div>
      </div>
    </p-card>
  </div>
  }
  @if (inputsFile.parsed || historicalFile.parsed) {

  <p-card styleClass="rounded-3xl shadow-xl border border-white bg-white/95 backdrop-blur-sm overflow-hidden">
    <ng-template pTemplate="header">
      <div class="px-6 pt-6 pb-2 flex flex-col gap-3">
        <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h3 class="text-lg font-bold text-slate-900 m-0">File Preview</h3>
            <p class="text-xs text-slate-500 m-0">Search, sort, filter, and review highlighted validation cells.</p>
          </div>

          <div class="inline-flex items-center rounded-full border border-rose-100 bg-rose-50 p-1 shadow-sm">
            @if (inputsFile.parsed) {

            <button type="button" class="rounded-full px-4 py-1.5 text-xs font-semibold transition-all duration-200"
              [class.bg-gradient-to-r]="activePreview === 'inputs'" [class.from-rose-500]="activePreview === 'inputs'"
              [class.to-fuchsia-500]="activePreview === 'inputs'" [class.text-white]="activePreview === 'inputs'"
              [class.shadow-sm]="activePreview === 'inputs'" [class.text-slate-600]="activePreview !== 'inputs'"
              (click)="switchPreview('inputs')">
              Inputs Time Series
            </button>
            }
            @if (historicalFile.parsed) {

            <button type="button" class="rounded-full px-4 py-1.5 text-xs font-semibold transition-all duration-200"
              [class.bg-gradient-to-r]="activePreview === 'historical'"
              [class.from-rose-500]="activePreview === 'historical'"
              [class.to-fuchsia-500]="activePreview === 'historical'"
              [class.text-white]="activePreview === 'historical'" [class.shadow-sm]="activePreview === 'historical'"
              [class.text-slate-600]="activePreview !== 'historical'" (click)="switchPreview('historical')">
              Historical Data
            </button>
            }
          </div>
        </div>

        <span class="p-input-icon-left w-full md:w-80">
          <input pInputText type="text" [ngModel]="globalFilterValue" (input)="applyGlobalFilter($event)"
            placeholder="Search all columns" class="w-full rounded-xl" />
        </span>
      </div>
    </ng-template>

    <div class="px-3 md:px-6 pb-6">
      @if (activePreview === 'inputs') {
      @if (inputsFile.rows.length) {
      <p-table #dtInputs [value]="inputsFile.rows" [columns]="inputsFile.columns" [paginator]="true" [rows]="10"
        [rowsPerPageOptions]="[10, 25, 50]" [showCurrentPageReport]="true"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
        [globalFilterFields]="inputsFile.columns.map(c => c.field)" sortMode="multiple"
        [tableStyle]="{ 'min-width': '60rem' }" styleClass="p-datatable-gridlines p-datatable-striped">
        <ng-template pTemplate="header">
          <tr>
            @for (col of inputsFile.columns; track col.field) {
            <th pSortableColumn="{{ col.field }}">
              <div class="flex items-center gap-2">
                <span>{{ col.header }}</span>
                <p-sortIcon field="{{ col.field }}"></p-sortIcon>
              </div>
            </th>
            }
          </tr>
          <tr>
            @for (col of inputsFile.columns; track col.field) {
            <th>
              <p-columnFilter type="text" field="{{ col.field }}" display="menu" [showMatchModes]="true"
                [showOperator]="false" [showAddButton]="false"></p-columnFilter>
            </th>
            }
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-rowData>
          <tr>
            @for (col of inputsFile.columns; track col.field) {
            <td [ngClass]="getCellErrorClass('inputs_time_series', rowData, col.field)"
              [title]="getCellErrorTitle('inputs_time_series', rowData, col.field)">
              {{ rowData[col.field] }}
            </td>
            }
          </tr>
        </ng-template>

        <ng-template pTemplate="emptymessage">
          <tr>
            <td [attr.colspan]="inputsFile.columns.length || 1" class="text-center py-8 text-slate-500">
              No parsed data available.
            </td>
          </tr>
        </ng-template>
      </p-table>
      } @else {
      <div class="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-slate-500">
        <i class="pi pi-file-excel text-3xl mb-3 text-slate-400"></i>
        <div class="text-sm font-medium">No Inputs Time Series file uploaded yet</div>
        <div class="text-xs mt-1">Upload a CSV or Excel file to parse and preview the data.</div>
      </div>
      }
      }

      @if (activePreview === 'historical') {
      @if (historicalFile.rows.length) {
      <p-table #dtHistorical [value]="historicalFile.rows" [columns]="historicalFile.columns" [paginator]="false"
        [globalFilterFields]="historicalFile.columns.map(c => c.field)" sortMode="multiple"
        [tableStyle]="{ 'min-width': '60rem' }" styleClass="p-datatable-gridlines p-datatable-striped">
        <ng-template pTemplate="header">
          <tr>
            @for (col of historicalFile.columns; track col.field) {
            <th pSortableColumn="{{ col.field }}">
              <div class="flex items-center gap-2">
                <span>{{ col.header }}</span>
                <p-sortIcon field="{{ col.field }}"></p-sortIcon>
              </div>
            </th>
            }
          </tr>
          <tr>
            @for (col of historicalFile.columns; track col.field) {
            <th>
              <p-columnFilter type="text" field="{{ col.field }}" display="menu" [showMatchModes]="true"
                [showOperator]="false" [showAddButton]="false"></p-columnFilter>
            </th>
            }
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-rowData>
          <tr>
            @for (col of historicalFile.columns; track col.field) {
            <td [ngClass]="getCellErrorClass('historical_data', rowData, col.field)"
              [title]="getCellErrorTitle('historical_data', rowData, col.field)">
              {{ rowData[col.field] }}
            </td>
            }
          </tr>
        </ng-template>

        <ng-template pTemplate="emptymessage">
          <tr>
            <td [attr.colspan]="historicalFile.columns.length || 1" class="text-center py-8 text-slate-500">
              No parsed data available.
            </td>
          </tr>
        </ng-template>
      </p-table>
      } @else {
      <div class="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-slate-500">
        <i class="pi pi-file-excel text-3xl mb-3 text-slate-400"></i>
        <div class="text-sm font-medium">No Historical Data file uploaded yet</div>
        <div class="text-xs mt-1">Upload a CSV or Excel file to parse and preview the data.</div>
      </div>
      }
      }
    </div>
  </p-card>
  }

  <p-card styleClass="rounded-3xl shadow-xl border border-white bg-white/95 backdrop-blur-sm overflow-hidden">
    <div class="px-6 pt-6 pb-6">
      <div class="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div class="rounded-3xl border border-sky-100 bg-gradient-to-br from-sky-50 via-white to-white p-5 shadow-sm">
          <div class="flex items-center justify-between mb-4">
            <div>
              <div class="text-sm font-bold text-slate-900">Inputs Time Series</div>
              <div class="text-xs text-slate-500">Uploaded file summary</div>
            </div>
            <span class="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-sky-100 text-sky-700">
              <i class="pi pi-database"></i>
            </span>
          </div>

          <div class="space-y-3">
            <div class="flex items-center justify-between">
              <span class="text-xs text-slate-500">Status</span>
              <p-tag
                [severity]="inputsFile.parsed ? hasFileErrors('inputs_time_series') ? 'danger' : 'success' : 'warn'"
                [value]="inputsFile.parsed ? getFileStatusLabel('inputs_time_series') : 'Pending'"></p-tag>
            </div>
            <div class="flex items-center justify-between gap-3">
              <span class="text-xs text-slate-500">File</span>
              <span class="text-xs font-semibold text-slate-700 text-right truncate max-w-[12rem]">{{
                inputsFile.fileName || '-' }}</span>
            </div>
            <div class="grid grid-cols-2 gap-3">
              <div class="rounded-2xl bg-white border border-sky-100 p-3">
                <div class="text-[11px] text-slate-500">Rows</div>
                <div class="text-lg font-bold text-slate-900">{{ inputsFile.rows.length || 0 }}</div>
              </div>
              <div class="rounded-2xl bg-white border border-sky-100 p-3">
                <div class="text-[11px] text-slate-500">Columns</div>
                <div class="text-lg font-bold text-slate-900">{{ inputsFile.columns.length || 0 }}</div>
              </div>
            </div>
          </div>
        </div>

        <div
          class="rounded-3xl border border-fuchsia-100 bg-gradient-to-br from-fuchsia-50 via-white to-white p-5 shadow-sm">
          <div class="flex items-center justify-between mb-4">
            <div>
              <div class="text-sm font-bold text-slate-900">Historical Data</div>
              <div class="text-xs text-slate-500">Uploaded file summary</div>
            </div>
            <span class="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-fuchsia-100 text-fuchsia-700">
              <i class="pi pi-history"></i>
            </span>
          </div>

          <div class="space-y-3">
            <div class="flex items-center justify-between">
              <span class="text-xs text-slate-500">Status</span>
              <p-tag
                [severity]="historicalFile.parsed ? hasFileErrors('historical_data') ? 'danger' : 'success' : 'warn'"
                [value]="historicalFile.parsed ? getFileStatusLabel('historical_data') : 'Pending'"></p-tag>
            </div>
            <div class="flex items-center justify-between gap-3">
              <span class="text-xs text-slate-500">File</span>
              <span class="text-xs font-semibold text-slate-700 text-right truncate max-w-[12rem]">{{
                historicalFile.fileName || '-' }}</span>
            </div>
            <div class="grid grid-cols-2 gap-3">
              <div class="rounded-2xl bg-white border border-fuchsia-100 p-3">
                <div class="text-[11px] text-slate-500">Rows</div>
                <div class="text-lg font-bold text-slate-900">{{ historicalFile.rows.length || 0 }}</div>
              </div>
              <div class="rounded-2xl bg-white border border-fuchsia-100 p-3">
                <div class="text-[11px] text-slate-500">Columns</div>
                <div class="text-lg font-bold text-slate-900">{{ historicalFile.columns.length || 0 }}</div>
              </div>
            </div>
          </div>
        </div>

        <div
          class="rounded-3xl border border-amber-100 bg-gradient-to-br from-amber-50 via-white to-white p-5 shadow-sm">
          <div class="flex items-center justify-between mb-4">
            <div>
              <div class="text-sm font-bold text-slate-900">Validation</div>
              <div class="text-xs text-slate-500">Quality checks summary</div>
            </div>
            <span class="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
              <i class="pi pi-shield"></i>
            </span>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div class="rounded-2xl bg-white border border-rose-100 p-3">
              <div class="text-[11px] text-slate-500">Errors</div>
              <div class="text-lg font-bold text-rose-700">{{ blockingIssueCountLabel }}</div>
            </div>
            <div class="rounded-2xl bg-white border border-amber-100 p-3">
              <div class="text-[11px] text-slate-500">Warnings</div>
              <div class="text-lg font-bold text-amber-700">{{ warningIssueCountLabel }}</div>
            </div>
          </div>

          <div class="mt-3 flex items-center justify-between">
            <span class="text-xs text-slate-500">Ready to Submit</span>
            <p-tag [severity]="canSubmit ? 'success' : 'danger'" [value]="canSubmit ? 'Yes' : 'No'"></p-tag>
          </div>

          @if (blockingIssues.length || warningIssues.length) {
          <button type="button"
            class="mt-3 text-xs font-semibold text-rose-600 underline underline-offset-4 hover:text-fuchsia-600"
            (click)="openIssuesDialog()">
            View validation issues
          </button>
          }
        </div>
      </div>
    </div>
  </p-card>
  }
</div>

<p-dialog header="Validation Results" [(visible)]="issuesDialogVisible" [modal]="true" [draggable]="false"
  [resizable]="false" [style]="{ width: '58rem', maxWidth: '95vw' }"
  [contentStyle]="{ padding: '0', overflow: 'hidden' }">
  <div class="bg-slate-50">
    <div class="px-5 py-4 border-b border-slate-200 bg-white">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div class="text-sm font-semibold text-slate-900">File validation summary</div>
          <div class="text-xs text-slate-500 mt-1">
            Review frontend and backend validation issues. Highlighted cells are shown in the preview table.
          </div>
        </div>

        <div class="flex items-center gap-2 text-xs">
          <span class="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2.5 py-1 font-semibold text-rose-700">
            <i class="pi pi-exclamation-triangle text-xs"></i>
            {{ blockingIssues.length }} Errors
          </span>
          <span
            class="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 font-semibold text-amber-700">
            <i class="pi pi-info-circle text-xs"></i>
            {{ warningIssues.length }} Warnings
          </span>
        </div>
      </div>
    </div>

    <div class="max-h-[65vh] overflow-y-auto px-5 py-5">
      @if (!hasAnyDialogIssues) {
      <div class="rounded-2xl border border-emerald-100 bg-emerald-50 p-5 text-center">
        <div
          class="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
          <i class="pi pi-check text-lg"></i>
        </div>
        <div class="text-sm font-semibold text-emerald-800">No validation issues found</div>
        <div class="mt-1 text-xs text-emerald-700">Uploaded files passed the current checks.</div>
      </div>
      }

      @if (inputDialogIssues.length) {
      <div class="mb-5 rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div class="flex items-center justify-between gap-3 border-b border-slate-100 bg-slate-50 px-4 py-3">
          <div>
            <div class="text-sm font-semibold text-slate-900">Inputs Time Series</div>
            <div class="text-xs text-slate-500">{{ inputsFile.fileName || 'No file selected' }}</div>
          </div>

          <div class="flex items-center gap-2 text-xs">
            @if (inputErrorCount) {
            <span class="rounded-full bg-rose-100 px-2.5 py-1 font-semibold text-rose-700">
              {{ inputErrorCount }} Errors
            </span>
            }
            @if (inputWarningCount) {
            <span class="rounded-full bg-amber-100 px-2.5 py-1 font-semibold text-amber-700">
              {{ inputWarningCount }} Warnings
            </span>
            }
          </div>
        </div>

        <div class="divide-y divide-slate-100">
          @for (issue of inputDialogIssueGroups; track issue.code + issue.message + issue.year + issue.backendKey) {
          <div class="px-4 py-3">
            <div class="flex items-start gap-3">
              <span class="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-xl"
                [class.bg-rose-100]="issue.severity === 'error'" [class.text-rose-700]="issue.severity === 'error'"
                [class.bg-amber-100]="issue.severity === 'warn'" [class.text-amber-700]="issue.severity === 'warn'"
                [class.bg-sky-100]="issue.severity !== 'error' && issue.severity !== 'warn'"
                [class.text-sky-700]="issue.severity !== 'error' && issue.severity !== 'warn'">
                <i class="pi text-xs" [class.pi-exclamation-triangle]="issue.severity === 'error'"
                  [class.pi-info-circle]="issue.severity === 'warn'"
                  [class.pi-bell]="issue.severity !== 'error' && issue.severity !== 'warn'"></i>
              </span>

              <div class="min-w-0 flex-1">
                <div class="text-sm font-semibold text-slate-800">
                  {{ issue.message }}
                </div>

                <div class="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-500">
                  <span class="rounded-full bg-slate-100 px-2 py-0.5">{{ issue.code }}</span>

                  @if (issue.source) {
                  <span class="rounded-full bg-slate-100 px-2 py-0.5">{{ issue.source }}</span>
                  }

                  @if (issue.year) {
                  <span class="rounded-full bg-slate-100 px-2 py-0.5">Year {{ issue.year }}</span>
                  }

                  @if (issue.variables.length) {
                  <span class="rounded-full bg-rose-50 px-2 py-0.5 font-semibold text-rose-700">
                    {{ issue.variables.length }} affected cell{{ issue.variables.length > 1 ? 's' : '' }}
                  </span>
                  }
                </div>

                @if (issue.displayVariables.length) {
                <div class="mt-2 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  <div class="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Affected rows
                  </div>

                  <div class="flex flex-wrap gap-1.5">
                    @for (variable of issue.displayVariables; track variable) {
                    <span class="rounded-full bg-white px-2 py-1 text-[11px] font-medium text-slate-600 shadow-sm">
                      {{ variable }}
                    </span>
                    }

                    @if (issue.hiddenVariableCount > 0) {
                    <span class="rounded-full bg-white px-2 py-1 text-[11px] font-semibold text-slate-500 shadow-sm">
                      +{{ issue.hiddenVariableCount }} more
                    </span>
                    }
                  </div>
                </div>
                }
              </div>
            </div>
          </div>
          }
        </div>
      </div>
      }

      @if (historicalDialogIssues.length) {
      <div class="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div class="flex items-center justify-between gap-3 border-b border-slate-100 bg-slate-50 px-4 py-3">
          <div>
            <div class="text-sm font-semibold text-slate-900">Historical Data</div>
            <div class="text-xs text-slate-500">{{ historicalFile.fileName || 'No file selected' }}</div>
          </div>

          <div class="flex items-center gap-2 text-xs">
            @if (historicalErrorCount) {
            <span class="rounded-full bg-rose-100 px-2.5 py-1 font-semibold text-rose-700">
              {{ historicalErrorCount }} Errors
            </span>
            }
            @if (historicalWarningCount) {
            <span class="rounded-full bg-amber-100 px-2.5 py-1 font-semibold text-amber-700">
              {{ historicalWarningCount }} Warnings
            </span>
            }
          </div>
        </div>

        <div class="divide-y divide-slate-100">
          @for (issue of historicalDialogIssueGroups; track issue.code + issue.message + issue.year + issue.backendKey)
          {
          <div class="px-4 py-3">
            <div class="flex items-start gap-3">
              <span class="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-xl"
                [class.bg-rose-100]="issue.severity === 'error'" [class.text-rose-700]="issue.severity === 'error'"
                [class.bg-amber-100]="issue.severity === 'warn'" [class.text-amber-700]="issue.severity === 'warn'"
                [class.bg-sky-100]="issue.severity !== 'error' && issue.severity !== 'warn'"
                [class.text-sky-700]="issue.severity !== 'error' && issue.severity !== 'warn'">
                <i class="pi text-xs" [class.pi-exclamation-triangle]="issue.severity === 'error'"
                  [class.pi-info-circle]="issue.severity === 'warn'"
                  [class.pi-bell]="issue.severity !== 'error' && issue.severity !== 'warn'"></i>
              </span>

              <div class="min-w-0 flex-1">
                <div class="text-sm font-semibold text-slate-800">
                  {{ issue.message }}
                </div>

                <div class="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-500">
                  <span class="rounded-full bg-slate-100 px-2 py-0.5">{{ issue.code }}</span>

                  @if (issue.source) {
                  <span class="rounded-full bg-slate-100 px-2 py-0.5">{{ issue.source }}</span>
                  }

                  @if (issue.year) {
                  <span class="rounded-full bg-slate-100 px-2 py-0.5">Year {{ issue.year }}</span>
                  }

                  @if (issue.variables.length) {
                  <span class="rounded-full bg-rose-50 px-2 py-0.5 font-semibold text-rose-700">
                    {{ issue.variables.length }} affected cell{{ issue.variables.length > 1 ? 's' : '' }}
                  </span>
                  }
                </div>

                @if (issue.displayVariables.length) {
                <div class="mt-2 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  <div class="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Affected rows
                  </div>

                  <div class="flex flex-wrap gap-1.5">
                    @for (variable of issue.displayVariables; track variable) {
                    <span class="rounded-full bg-white px-2 py-1 text-[11px] font-medium text-slate-600 shadow-sm">
                      {{ variable }}
                    </span>
                    }

                    @if (issue.hiddenVariableCount > 0) {
                    <span class="rounded-full bg-white px-2 py-1 text-[11px] font-semibold text-slate-500 shadow-sm">
                      +{{ issue.hiddenVariableCount }} more
                    </span>
                    }
                  </div>
                </div>
                }
              </div>
            </div>
          </div>
          }
        </div>
      </div>
      }
    </div>
  </div>

  <ng-template pTemplate="footer">
    <button pButton type="button" label="Close" class="p-button-outlined"
      (click)="issuesDialogVisible = false"></button>
  </ng-template>
</p-dialog>

<p-dialog header="Replace Existing File" [(visible)]="replaceDialogVisible" [modal]="true" [draggable]="false"
  [resizable]="false" [closable]="false" [style]="{ width: '28rem', maxWidth: '95vw' }">
  <div class="text-sm text-slate-700">
A city_level_historical_data file is already uploaded. Do you want to replace the existing file?
  </div>

  <ng-template pTemplate="footer">
    <button pButton type="button" label="Cancel" class="p-button-outlined" (click)="cancelReplaceFile()"></button>

    <button pButton type="button" label="Replace" icon="pi pi-refresh"
      class="bg-gradient-to-r from-rose-500 to-fuchsia-500 border-0" (click)="confirmReplaceFile()"></button>
  </ng-template>
</p-dialog>